const DELIVERY_CHANNELS = new Set(["email", "sms", "whatsapp"]);
const COMPLETION_SECRET_PATTERN = /^[A-Za-z0-9_-]{32,}$/;

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && Boolean(value.trim());
}

function isDeliveryChannel(value) {
  return typeof value === "string" && DELIVERY_CHANNELS.has(value);
}

function hasOwn(record, key) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * Returns a fully validated pending contact sign-in response, null for a normal
 * token response, and throws for a malformed response that claims verification
 * is required. That last distinction keeps mixed or partial 2xx responses from
 * ever falling through to session creation.
 */
export function readPendingContactSignIn(value) {
  if (!isRecord(value)) return null;
  const hasPendingSignal = value.pendingSignIn === true || value.verificationRequired === true;
  if (!hasPendingSignal) return null;

  const hasForbiddenSessionMaterial = [
    "accessToken",
    "refreshToken",
    "refreshExpiresAt",
    "sessionId",
    "accessExpiresIn",
    "contactVerificationDeviceCleared",
    "phoneVerificationDeviceCleared",
    "signInApproved",
    "returnToSignIn",
    "error",
    "code",
    "verified",
    "purpose",
    "user",
  ].some((key) => hasOwn(value, key));
  const exactDeliveryContract =
    (value.identityType === "email" &&
      value.deliveryChannel === "email" &&
      value.primaryDeliveryChannel === "email" &&
      value.completionMode === "otp_code" &&
      !hasOwn(value, "approvalState") &&
      !hasOwn(value, "pollAfterMs")) ||
    (value.identityType === "phone" &&
      value.deliveryChannel === "whatsapp" &&
      value.primaryDeliveryChannel === "whatsapp" &&
      value.completionMode === "link_approval" &&
      value.approvalState === "pending" &&
      Number.isFinite(value.pollAfterMs) &&
      value.pollAfterMs > 0) ||
    (value.identityType === "phone" &&
      value.deliveryChannel === "sms" &&
      value.primaryDeliveryChannel === "sms" &&
      value.completionMode === "otp_code" &&
      !hasOwn(value, "approvalState") &&
      !hasOwn(value, "pollAfterMs"));
  const valid =
    !hasForbiddenSessionMaterial &&
    value.ok === true &&
    value.pendingSignIn === true &&
    value.verificationRequired === true &&
    (value.identityType === "email" || value.identityType === "phone") &&
    isNonEmptyString(value.challengeId) &&
    typeof value.completionSecret === "string" &&
    COMPLETION_SECRET_PATTERN.test(value.completionSecret) &&
    Number.isFinite(value.expiresInSec) &&
    value.expiresInSec > 0 &&
    isNonEmptyString(value.maskedTarget) &&
    isDeliveryChannel(value.deliveryChannel) &&
    isDeliveryChannel(value.primaryDeliveryChannel) &&
    value.fallbackDeliveryChannel === null &&
    exactDeliveryContract &&
    !hasOwn(value, "whatsappVerificationMode") &&
    !hasOwn(value, "whatsappFallbackAvailable") &&
    !hasOwn(value, "whatsappFallbackEngaged") &&
    !hasOwn(value, "whatsappFallbackAutomatic") &&
    !hasOwn(value, "voiceFallbackAvailable") &&
    !hasOwn(value, "voiceFallbackEngaged");

  if (!valid) {
    const error = new Error("invalid_contact_sign_in_response");
    error.code = "invalid_contact_sign_in_response";
    throw error;
  }
  return value;
}

export function isLinkApprovalContactSignIn(pending) {
  return (
    pending.identityType === "phone" &&
    pending.deliveryChannel === "whatsapp" &&
    pending.completionMode === "link_approval"
  );
}
